import { useState, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Upload, Loader2, Plus, Check, ImageIcon } from "lucide-react";

const MultiImageUploader = ({ images = [], onChange, maxImages = 10 }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remaining = maxImages - images.length;
    if (files.length > remaining) {
      toast.error(`You can only add ${remaining} more image(s)`);
      return;
    }

    setUploading(true);
    const newUrls = [];

    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 10MB limit`);
        continue;
      }
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await axios.post(`${API}/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newUrls.push(res.data.url);
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    if (newUrls.length > 0) {
      onChange([...images, ...newUrls]);
      toast.success(`${newUrls.length} image(s) uploaded`);
    }

    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const toggleImage = (index) => {
    onChange(images.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3" data-testid="multi-image-uploader">
      {images.length > 0 && (
        <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
          {images.map((url, i) => (
            <button
              key={`${url}-${i}`}
              type="button"
              onClick={() => toggleImage(i)}
              className="relative aspect-square rounded-lg overflow-hidden border-2 border-emerald-500/60 bg-[#030712] group cursor-pointer transition-all hover:border-red-500/60"
              data-testid={`uploaded-image-${i}`}
              title="Uncheck to remove"
            >
              <img
                src={url}
                alt={`Image ${i + 1}`}
                className="w-full h-full object-cover"
              />
              {/* Checked overlay */}
              <div className="absolute top-1.5 left-1.5 w-5 h-5 rounded bg-emerald-500 flex items-center justify-center shadow-md group-hover:bg-red-500 transition-colors" data-testid={`image-checkbox-${i}`}>
                <Check className="w-3.5 h-3.5 text-white" />
              </div>
              {/* MAIN badge */}
              {i === 0 && (
                <span className="absolute bottom-1 left-1 text-[9px] font-mono bg-amber-500 text-black px-1.5 py-0.5 rounded font-bold">
                  MAIN
                </span>
              )}
              {/* Hover hint */}
              <div className="absolute inset-0 bg-red-500/0 group-hover:bg-red-500/10 transition-colors" />
            </button>
          ))}

          {/* Add More Slot */}
          {images.length < maxImages && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="aspect-square rounded-lg border-2 border-dashed border-white/10 hover:border-amber-500/40 flex flex-col items-center justify-center gap-1 text-slate-500 hover:text-amber-500 transition-colors"
              data-testid="add-more-images-btn"
            >
              {uploading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  <span className="text-[10px]">Add</span>
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Empty State */}
      {images.length === 0 && (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-full py-6 rounded-lg border-2 border-dashed border-white/10 hover:border-amber-500/30 flex flex-col items-center justify-center gap-2 text-slate-500 hover:text-amber-400 transition-colors"
          data-testid="upload-images-btn"
        >
          {uploading ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            <>
              <Upload className="w-6 h-6" />
              <span className="text-sm">Click to upload images</span>
              <span className="text-[10px] text-slate-600">Up to {maxImages} images, max 10MB each</span>
            </>
          )}
        </button>
      )}

      <p className="text-[10px] text-slate-600">
        {images.length}/{maxImages} images. Click an image to remove it. First image is the cover.
      </p>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleUpload}
        className="hidden"
        data-testid="image-file-input"
      />
    </div>
  );
};

export default MultiImageUploader;
